import { useCallback, useEffect, useState } from 'react';
import type { Eu, Falha } from '../../acesso';
import { podeFazer } from '../../acesso';
import type { Sessao } from '../../auth';
import { SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import type { Escrita } from '../../giro';
import { saudacao } from '../../shell/ShellBalconista';
import { lerAvisos, mudarAviso, salvarAviso } from '../../administracao';
import type { Aviso, EstadoDoAviso, PublicoDoAviso, TipoDeAviso } from '../../administracao';
import { ErroDaLista, Esqueleto, useEscape } from '../equipe/Equipe';
import {
  CartaoDeAviso,
  Desfecho,
  IconeDoTipo,
  IconeExtra,
  ROTULO_DO_PUBLICO,
  ROTULO_DO_TIPO,
  deCampoDataHora,
  diaMesHora,
  paraCampoDataHora,
  vigencia,
} from './comunicacao-comum';
import '../equipe/acesso.css';
import './mural.css';

/**
 * TELA 30 — COMUNICAÇÃO E MURAL. "O Mural informa. Não atrapalha o trabalho."
 *
 * Comunicado interno: sem conversa, sem grupo, sem mensagem direta. Publicar exige "até quando
 * vale"; publicado não se reescreve (arquive e publique outro); arquivar pede motivo. Visto, lido
 * e confirmado são três fatos diferentes, contados pelo servidor. Urgente é exceção.
 */

type Carga =
  | { readonly fase: 'carregando' }
  | { readonly fase: 'erro'; readonly falha: Falha }
  | {
      readonly fase: 'pronto';
      readonly avisos: readonly Aviso[];
      readonly contagem: Readonly<Record<EstadoDoAviso, number>>;
    };
type Filtro = 'ativos' | 'programados' | 'rascunho' | 'historico';

const NO_FILTRO: Readonly<Record<Filtro, readonly EstadoDoAviso[]>> = {
  // A área ativa: tudo o que ainda não expirou nem foi arquivado (como no desenho).
  ativos: ['PUBLICADO', 'PROGRAMADO', 'RASCUNHO'],
  programados: ['PROGRAMADO'],
  rascunho: ['RASCUNHO'],
  historico: ['EXPIRADO', 'ARQUIVADO'],
};
const TIPOS_COMUNS: readonly Exclude<TipoDeAviso, 'URGENTE'>[] = [
  'INFORMACAO',
  'ATENCAO',
  'CAMPANHA',
  'OPERACIONAL',
];

export function Mural({
  sessao,
  eu,
  parametros,
}: {
  sessao: Sessao;
  eu: Eu | null;
  parametros: URLSearchParams;
}) {
  const [carga, setCarga] = useState<Carga>({ fase: 'carregando' });
  const [filtro, setFiltro] = useState<Filtro>('ativos');
  const [tipo, setTipo] = useState<TipoDeAviso | ''>('');
  const [publico, setPublico] = useState<PublicoDoAviso | ''>('');
  const [formulario, setFormulario] = useState<{ aviso: Aviso | null } | null>(null);
  const [aberto, setAberto] = useState<string | null>(parametros.get('aviso'));
  const publica = podeFazer(eu, 'comunicacao.publicar');

  const carregar = useCallback(async () => {
    const r = await lerAvisos();
    setCarga(
      r.ok
        ? { fase: 'pronto', avisos: r.dados.avisos, contagem: r.dados.contagem }
        : { fase: 'erro', falha: r.falha },
    );
  }, []);
  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (carga.fase === 'erro' && carga.falha.tipo === 'sem-acesso')
    return <SemAcesso area="Comunicação" />;

  const dados = carga.fase === 'pronto' ? carga : null;
  const c = dados?.contagem;
  const visiveis =
    dados?.avisos
      .filter(
        (a) =>
          NO_FILTRO[filtro].includes(a.estado) &&
          (!tipo || a.tipo === tipo) &&
          (!publico || a.publico === publico),
      )
      // A ordem é a do servidor (urgente e fixado primeiro); rascunho, ainda sem data, vai ao fim.
      .sort((x, y) => Number(x.estado === 'RASCUNHO') - Number(y.estado === 'RASCUNHO')) ?? [];
  const urgentes = dados?.avisos.filter(
    (a) => a.tipo === 'URGENTE' && a.estado === 'PUBLICADO',
  ).length;
  const avisoAberto = dados?.avisos.find((a) => a.id === aberto) ?? null;
  const chips: readonly (readonly [Filtro, string, number | undefined])[] = [
    ['ativos', 'Ativos', c ? c.PUBLICADO + c.PROGRAMADO + c.RASCUNHO : undefined],
    ['programados', 'Programados', c?.PROGRAMADO],
    ['rascunho', 'Rascunho', c?.RASCUNHO],
    ['historico', 'Expirados e arquivados', c ? c.EXPIRADO + c.ARQUIVADO : undefined],
  ];

  return (
    <div className="ac ac-cheia mu">
      <div className="ac-cabeca">
        <div>
          <h1 className="ac-titulo">Mural e avisos</h1>
          <div className="ac-subtitulo">
            Comunicados internos · sem conversa, sem grupo, sem mensagem direta
          </div>
        </div>
        <div className="ac-acoes">
          <button type="button" className="ac-botao" onClick={() => setFiltro('historico')}>
            Histórico
          </button>
          {publica && (
            <button
              type="button"
              className="ac-botao ac-botao-primario"
              onClick={() => setFormulario({ aviso: null })}
            >
              <Icone nome="plus" tamanho={14} traco={1.8} />
              Novo aviso
            </button>
          )}
        </div>
      </div>

      <div className="ac-filtros">
        {chips.map(([id, rotulo, n]) => (
          <button
            key={id}
            type="button"
            aria-pressed={filtro === id}
            className={`ac-chip${filtro === id ? ' ac-chip-ativo' : ''}`}
            onClick={() => setFiltro(id)}
          >
            {rotulo}
            {n !== undefined ? ` ${String(n)}` : ''}
          </button>
        ))}
        <span className="ac-divisor" />
        <label className="ac-seletor">
          <span className="ac-seletor-rotulo">Tipo</span>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoDeAviso | '')}
            aria-label="Tipo"
          >
            <option value="">Todos</option>
            {(Object.keys(ROTULO_DO_TIPO) as TipoDeAviso[]).map((t) => (
              <option key={t} value={t}>
                {ROTULO_DO_TIPO[t]}
              </option>
            ))}
          </select>
          <Icone nome="chev-d" tamanho={12} traco={1.6} />
        </label>
        <label className="ac-seletor">
          <span className="ac-seletor-rotulo">Público</span>
          <select
            value={publico}
            onChange={(e) => setPublico(e.target.value as PublicoDoAviso | '')}
            aria-label="Público"
          >
            <option value="">Todos</option>
            {(Object.keys(ROTULO_DO_PUBLICO) as PublicoDoAviso[]).map((p) => (
              <option key={p} value={p}>
                {ROTULO_DO_PUBLICO[p]}
              </option>
            ))}
          </select>
          <Icone nome="chev-d" tamanho={12} traco={1.6} />
        </label>
        <span className="ac-espaco" />
        {urgentes !== undefined && (
          <span className="ac-nota">
            {urgentes === 0
              ? 'nenhum urgente agora'
              : `${String(urgentes)} ${urgentes === 1 ? 'urgente' : 'urgentes'} agora`}{' '}
            · urgência é exceção
          </span>
        )}
      </div>

      <div className="ac-cartao ac-cartao-tabela">
        {carga.fase === 'carregando' && <Esqueleto linhas={6} />}
        {carga.fase === 'erro' && (
          <div style={{ padding: 16 }}>
            <ErroDaLista
              titulo="Não foi possível carregar os avisos"
              texto="Nada foi publicado nem arquivado. Tente novamente."
              aoTentar={() => void carregar()}
            />
          </div>
        )}
        {dados && visiveis.length === 0 && (
          <div className="ac-vazio">
            <div className="ac-vazio-titulo">
              {dados.avisos.length === 0
                ? 'Nenhum aviso publicado'
                : filtro === 'ativos'
                  ? 'Nenhum aviso no ar agora'
                  : 'Nenhum aviso neste filtro'}
            </div>
            <div className="ac-vazio-texto">
              A área ativa fica vazia — sem banner de enfeite na Home.
            </div>
          </div>
        )}
        {visiveis.length > 0 && (
          <div className="ac-tabela-rolo">
            <table className="ac-tabela mu-tabela">
              <thead>
                <tr>
                  <th>AVISO</th>
                  <th>TIPO</th>
                  <th className="mu-col-larga">PÚBLICO</th>
                  <th>VIGÊNCIA</th>
                  <th className="mu-col-larga">PUBLICADO POR</th>
                  <th className="ac-direita">LEITURAS</th>
                  <th>STATUS</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {visiveis.map((a) => (
                  <LinhaDoAviso
                    key={a.id}
                    a={a}
                    publica={publica}
                    aoAbrir={() => setAberto(a.id)}
                    aoContinuar={() => setFormulario({ aviso: a })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="ac-rodape-tabela">
          <span>
            {dados
              ? `${String(visiveis.length)} de ${String(dados.avisos.length)} ${dados.avisos.length === 1 ? 'aviso' : 'avisos'}`
              : ' '}
          </span>
          <span>
            Leituras separam <b>visto</b> de <b>confirmado</b> · expirado vai para o histórico, não
            é apagado
          </span>
        </div>
      </div>

      {avisoAberto && (
        <DrawerDoAviso
          a={avisoAberto}
          sessao={sessao}
          publica={publica}
          aoFechar={() => setAberto(null)}
          aoContinuar={() => {
            setAberto(null);
            setFormulario({ aviso: avisoAberto });
          }}
          aoMudar={() => void carregar()}
        />
      )}
      {formulario && (
        <FormularioDoAviso
          aviso={formulario.aviso}
          sessao={sessao}
          aoFechar={() => setFormulario(null)}
          aoConcluir={(estado) => {
            setFormulario(null);
            setFiltro(estado === 'PUBLICADO' ? 'ativos' : 'rascunho');
            void carregar();
          }}
          aoConferir={() => {
            setFormulario(null);
            void carregar();
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------ tabela ------------------------------ */

export function SeloDoTipo({ tipo }: { tipo: TipoDeAviso }) {
  return (
    <span className={`mu-tipo mu-tipo-${tipo.toLowerCase()}`}>
      {tipo === 'URGENTE' && <Icone nome="warn" tamanho={11} traco={1.9} />}
      {ROTULO_DO_TIPO[tipo]}
    </span>
  );
}

const ROTULO_DO_ESTADO: Readonly<Record<EstadoDoAviso, string>> = {
  RASCUNHO: 'Rascunho',
  PROGRAMADO: 'Programado',
  PUBLICADO: 'Publicado',
  EXPIRADO: 'Expirado',
  ARQUIVADO: 'Arquivado',
};
function StatusDoAviso({ estado }: { estado: EstadoDoAviso }) {
  return (
    <span className={`mu-status mu-status-${estado.toLowerCase()}`}>
      {estado === 'PUBLICADO' && <span className="ac-status-ponto" />}
      {ROTULO_DO_ESTADO[estado]}
    </span>
  );
}

function Leituras({ a }: { a: Aviso }) {
  if (!a.publicadoEm) return <span className="mu-sem">—</span>;
  return (
    <>
      <div className="mu-leituras">
        {a.leituras.vistos} de {a.leituras.publico}
      </div>
      {a.exigeConfirmacao && (
        <div className="mu-leituras-sub">
          {a.leituras.confirmados} {a.leituras.confirmados === 1 ? 'confirmou' : 'confirmaram'}
        </div>
      )}
    </>
  );
}

function LinhaDoAviso({
  a,
  publica,
  aoAbrir,
  aoContinuar,
}: {
  a: Aviso;
  publica: boolean;
  aoAbrir: () => void;
  aoContinuar: () => void;
}) {
  const urgente = a.tipo === 'URGENTE' && a.estado === 'PUBLICADO';
  const rascunho = a.estado === 'RASCUNHO';
  const sub = a.campanhaId
    ? 'ligado a uma campanha'
    : a.exigeConfirmacao
      ? 'pede confirmação de leitura'
      : null;
  return (
    <tr className={urgente ? 'mu-linha-urgente' : rascunho ? 'mu-linha-rascunho' : undefined}>
      <td>
        <div className="mu-aviso">
          {a.fixado && (
            <span className="mu-pino" title="Fixado no topo">
              <IconeExtra nome="fixar" tamanho={13} traco={1.7} />
            </span>
          )}
          <div style={{ minWidth: 0 }}>
            <div className="mu-titulo">{a.titulo}</div>
            {sub && <div className="mu-sub">{sub}</div>}
          </div>
        </div>
      </td>
      <td>
        <SeloDoTipo tipo={a.tipo} />
      </td>
      <td className="ac-cinza mu-col-larga">{ROTULO_DO_PUBLICO[a.publico]}</td>
      <td className={`ac-num${a.inicio || a.fim ? '' : ' ac-fraco'}`}>
        {vigencia(a.inicio, a.fim)}
      </td>
      <td className="ac-cinza mu-col-larga">{a.publicadoPor ?? a.criadoPor ?? '—'}</td>
      <td className="ac-direita">
        <Leituras a={a} />
      </td>
      <td>
        <StatusDoAviso estado={a.estado} />
      </td>
      <td className="ac-direita">
        {rascunho && publica ? (
          <button type="button" className="ac-link" onClick={aoContinuar}>
            Continuar
          </button>
        ) : (
          <button type="button" className="ac-link" onClick={aoAbrir}>
            Abrir
          </button>
        )}
      </td>
    </tr>
  );
}

/* ------------------------------ detalhe do aviso ------------------------------ */

function DrawerDoAviso({
  a,
  sessao,
  publica,
  aoFechar,
  aoContinuar,
  aoMudar,
}: {
  a: Aviso;
  sessao: Sessao;
  publica: boolean;
  aoFechar: () => void;
  aoContinuar: () => void;
  aoMudar: () => void;
}) {
  const [arquivando, setArquivando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [escrita, setEscrita] = useState<Escrita<{ estado: string; versao: number }> | null>(null);
  useEscape(aoFechar, !enviando);
  const arquivado = a.estado === 'ARQUIVADO';

  const mudar = async (acao: 'publicar' | 'arquivar' | 'fixar' | 'desafixar') => {
    setEnviando(true);
    setEscrita(null);
    const r = await mudarAviso(
      sessao,
      a.id,
      acao,
      a.versao,
      acao === 'arquivar' ? motivo.trim() : null,
    );
    setEnviando(false);
    setEscrita(r);
    if (r.tipo === 'confirmada') {
      setArquivando(false);
      aoMudar();
    }
  };

  const l = a.leituras;
  return (
    <div
      className="ac-veu"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !enviando) aoFechar();
      }}
    >
      <aside className="ac-drawer mu-drawer" role="dialog" aria-modal="true" aria-label={a.titulo}>
        <div className="ac-drawer-cabeca">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="mu-drawer-selos">
              <SeloDoTipo tipo={a.tipo} />
              <StatusDoAviso estado={a.estado} />
              {a.fixado && <span className="mu-tipo">Fixado no topo</span>}
            </div>
            <div className="ac-drawer-nome mu-drawer-nome">{a.titulo}</div>
            <div className="ac-drawer-meta">
              <span>{ROTULO_DO_PUBLICO[a.publico]}</span>
              <span>{vigencia(a.inicio, a.fim)}</span>
              {a.publicadoPor && a.publicadoEm && (
                <span>
                  publicado por {a.publicadoPor} · {diaMesHora(a.publicadoEm)}
                </span>
              )}
            </div>
          </div>
          <button type="button" className="ac-fechar" onClick={aoFechar} aria-label="Fechar">
            <Icone nome="x" tamanho={15} traco={1.7} />
          </button>
        </div>

        <section className="ac-secao">
          <div className="mu-corpo">{a.corpo}</div>
        </section>

        <section className="ac-secao">
          <div className="ac-rotulo-mono">LEITURAS · TRÊS ESTADOS</div>
          <div className="mu-tres">
            <div className="mu-tres-linha">
              <Icone nome="eye" tamanho={15} traco={1.6} />
              <div style={{ flex: 1 }}>
                <div className="mu-tres-nome">Visto</div>
                <div className="mu-tres-sub">o aviso apareceu na tela</div>
              </div>
              <span className="mu-tres-n">{a.publicadoEm ? l.vistos : '—'}</span>
            </div>
            <div className="mu-tres-linha">
              <IconeExtra nome="info" tamanho={15} traco={1.6} />
              <div style={{ flex: 1 }}>
                <div className="mu-tres-nome">Lido</div>
                <div className="mu-tres-sub">abriu o detalhe</div>
              </div>
              <span className="mu-tres-n">{a.publicadoEm ? l.lidos : '—'}</span>
            </div>
            <div className="mu-tres-linha mu-tres-ok">
              <Icone nome="check" tamanho={15} traco={1.9} />
              <div style={{ flex: 1 }}>
                <div className="mu-tres-nome">Confirmado</div>
                <div className="mu-tres-sub">
                  {a.exigeConfirmacao
                    ? 'tocou em "li e entendi"'
                    : 'este aviso não pede confirmação'}
                </div>
              </div>
              <span className="mu-tres-n">
                {a.exigeConfirmacao && a.publicadoEm ? l.confirmados : '—'}
              </span>
            </div>
          </div>
          <div className="mu-pe">
            De {l.publico} {l.publico === 1 ? 'pessoa' : 'pessoas'} no público. O que conta como
            visto, lido e confirmado é definido pelo servidor — a tela apenas mostra os três.
          </div>
        </section>

        {publica && (
          <section className="ac-secao">
            <div className="ac-rotulo-mono">AÇÕES</div>
            {a.estado === 'RASCUNHO' ? (
              <div className="mu-acoes">
                <button
                  type="button"
                  className="ac-botao ac-botao-primario ac-botao-g"
                  onClick={aoContinuar}
                >
                  Continuar rascunho
                </button>
              </div>
            ) : arquivado ? (
              <div className="ac-nota">Arquivado: fica no histórico, só para consulta.</div>
            ) : (
              <div className="mu-acoes">
                <button
                  type="button"
                  className="ac-botao ac-botao-g"
                  disabled={enviando}
                  onClick={() => void mudar(a.fixado ? 'desafixar' : 'fixar')}
                >
                  <IconeExtra nome="fixar" tamanho={14} />
                  {a.fixado ? 'Desafixar' : 'Fixar no topo'}
                </button>
                <button
                  type="button"
                  className="ac-botao ac-botao-perigo ac-botao-g"
                  disabled={enviando}
                  onClick={() => setArquivando(true)}
                >
                  Arquivar
                </button>
              </div>
            )}
            {arquivando && (
              <div className="mu-arquivar">
                <label className="ac-campo-rotulo" htmlFor="mu-motivo">
                  Motivo do arquivamento — obrigatório
                </label>
                <textarea
                  id="mu-motivo"
                  className="ac-texto-area"
                  value={motivo}
                  maxLength={500}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Mínimo 5 caracteres. Fica registrado com seu nome e a hora."
                  autoFocus
                />
                <div className="ac-botoes-modal">
                  <button
                    type="button"
                    className="ac-botao-cheio"
                    disabled={motivo.trim().length < 5 || enviando}
                    onClick={() => void mudar('arquivar')}
                  >
                    {enviando ? 'Arquivando…' : 'Confirmar arquivamento'}
                  </button>
                  <button
                    type="button"
                    className="ac-botao-contorno"
                    onClick={() => setArquivando(false)}
                  >
                    Voltar
                  </button>
                </div>
              </div>
            )}
            <Desfecho
              escrita={escrita}
              incerta="Pode ter sido aplicado. Feche e confira a lista antes de repetir."
              aoConferir={() => {
                aoMudar();
                aoFechar();
              }}
            />
            <div className="mu-regra">
              Aviso publicado <b>não se reescreve</b>: para corrigir, arquive e publique outro.
              Preferir arquivar a excluir — o histórico importa.
            </div>
          </section>
        )}
      </aside>
    </div>
  );
}

/* ------------------------------ novo aviso + prévia ------------------------------ */

function FormularioDoAviso({
  aviso,
  sessao,
  aoFechar,
  aoConcluir,
  aoConferir,
}: {
  aviso: Aviso | null;
  sessao: Sessao;
  aoFechar: () => void;
  aoConcluir: (estado: 'RASCUNHO' | 'PUBLICADO') => void;
  aoConferir: () => void;
}) {
  const [titulo, setTitulo] = useState(aviso?.titulo ?? '');
  const [corpo, setCorpo] = useState(aviso?.corpo ?? '');
  const [tipo, setTipo] = useState<Exclude<TipoDeAviso, 'URGENTE'>>(
    aviso && aviso.tipo !== 'URGENTE' ? aviso.tipo : 'INFORMACAO',
  );
  const [urgente, setUrgente] = useState(aviso?.tipo === 'URGENTE');
  const [publico, setPublico] = useState<PublicoDoAviso>(aviso?.publico ?? 'BALCONISTAS');
  const [inicio, setInicio] = useState(paraCampoDataHora(aviso?.inicio ?? null));
  const [fim, setFim] = useState(paraCampoDataHora(aviso?.fim ?? null));
  const [fixado, setFixado] = useState(aviso?.fixado ?? false);
  const [confirmacao, setConfirmacao] = useState(aviso?.exigeConfirmacao ?? false);
  const [previa, setPrevia] = useState<'celular' | 'computador'>('celular');
  const [enviando, setEnviando] = useState<'rascunho' | 'publicar' | null>(null);
  const [falha, setFalha] = useState<{ escrita: Escrita<unknown>; etapa: string } | null>(null);
  // Depois do primeiro salvar, o rascunho existe: as próximas tentativas editam o mesmo.
  const [salvo, setSalvo] = useState<{ id: string; versao: number } | null>(
    aviso ? { id: aviso.id, versao: aviso.versao } : null,
  );
  useEscape(aoFechar, enviando === null);

  const tituloOk = titulo.trim().length >= 3 && titulo.trim().length <= 120;
  const corpoOk = corpo.trim().length >= 3;
  const periodoOk = !inicio || !fim || fim > inicio;
  const podeSalvar = tituloOk && corpoOk && periodoOk && enviando === null;
  const tipoFinal: TipoDeAviso = urgente ? 'URGENTE' : tipo;

  const enviar = async (publicar: boolean) => {
    setFalha(null);
    setEnviando(publicar ? 'publicar' : 'rascunho');
    const s = await salvarAviso(sessao, {
      id: salvo?.id ?? null,
      versao: salvo?.versao ?? null,
      titulo: titulo.trim(),
      corpo: corpo.trim(),
      tipo: tipoFinal,
      publico,
      inicio: deCampoDataHora(inicio),
      fim: deCampoDataHora(fim),
      fixado,
      exigeConfirmacao: confirmacao,
    });
    if (s.tipo !== 'confirmada') {
      setEnviando(null);
      setFalha({ escrita: s, etapa: 'salvar' });
      return;
    }
    setSalvo(s.dados);
    if (!publicar) {
      setEnviando(null);
      aoConcluir('RASCUNHO');
      return;
    }
    const p = await mudarAviso(sessao, s.dados.id, 'publicar', s.dados.versao);
    setEnviando(null);
    if (p.tipo === 'confirmada') aoConcluir('PUBLICADO');
    else setFalha({ escrita: p, etapa: 'publicar' });
  };

  const conteudo = {
    titulo: titulo.trim(),
    corpo: corpo.trim() || 'O texto do aviso aparece aqui.',
    tipo: tipoFinal,
    fixado,
    exigeConfirmacao: confirmacao,
    fim: deCampoDataHora(fim),
  };

  return (
    <div
      className="ac-veu ac-veu-centro mu-veu-form"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && enviando === null) aoFechar();
      }}
    >
      <div
        className="mu-form-painel"
        role="dialog"
        aria-modal="true"
        aria-label={aviso ? 'Continuar rascunho' : 'Novo aviso'}
      >
        <div className="mu-form">
          <div className="mu-form-cabeca">
            <div style={{ flex: 1 }}>
              <div className="mu-form-titulo">{aviso ? 'Continuar rascunho' : 'Novo aviso'}</div>
              <div className="ac-modal-sub">Rascunho · não aparece para ninguém até publicar</div>
            </div>
            <button type="button" className="ac-fechar" onClick={aoFechar} aria-label="Fechar">
              <Icone nome="x" tamanho={15} traco={1.7} />
            </button>
          </div>
          <div className="mu-form-corpo">
            <label className="ac-campo-rotulo" htmlFor="mu-titulo">
              Título
            </label>
            <input
              id="mu-titulo"
              className="mu-campo mu-campo-titulo"
              value={titulo}
              maxLength={120}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="O que a pessoa precisa saber, em uma linha"
              autoFocus
            />
            <label className="ac-campo-rotulo" htmlFor="mu-corpo">
              Mensagem
            </label>
            <textarea
              id="mu-corpo"
              className="mu-campo mu-campo-texto"
              value={corpo}
              maxLength={4000}
              onChange={(e) => setCorpo(e.target.value)}
              placeholder="Texto curto. Quem quiser o detalhe abre a campanha ou a regra."
            />
            <div className="mu-grade">
              <div>
                <label className="ac-campo-rotulo" htmlFor="mu-tipo">
                  Tipo
                </label>
                <span className="mu-select">
                  <select
                    id="mu-tipo"
                    className="mu-campo"
                    value={tipo}
                    onChange={(e) => setTipo(e.target.value as Exclude<TipoDeAviso, 'URGENTE'>)}
                  >
                    {TIPOS_COMUNS.map((t) => (
                      <option key={t} value={t}>
                        {ROTULO_DO_TIPO[t]}
                      </option>
                    ))}
                  </select>
                  <Icone nome="chev-d" tamanho={13} traco={1.6} />
                </span>
              </div>
              <div>
                <label className="ac-campo-rotulo" htmlFor="mu-prioridade">
                  Prioridade
                </label>
                <span className="mu-select">
                  <select
                    id="mu-prioridade"
                    className="mu-campo"
                    value={urgente ? 'urgente' : 'normal'}
                    onChange={(e) => setUrgente(e.target.value === 'urgente')}
                  >
                    <option value="normal">Normal</option>
                    <option value="urgente">Urgente — exceção</option>
                  </select>
                  <Icone nome="chev-d" tamanho={13} traco={1.6} />
                </span>
              </div>
            </div>
            <div className="ac-campo-rotulo">Público</div>
            <div className="mu-publico" role="group" aria-label="Público">
              {(['BALCONISTAS', 'TODOS', 'AUDITORES', 'ADMINISTRADORES'] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  aria-pressed={publico === p}
                  className={`mu-pilula${publico === p ? ' mu-pilula-ativa' : ''}`}
                  onClick={() => setPublico(p)}
                >
                  {p === 'ADMINISTRADORES' ? 'Admins' : ROTULO_DO_PUBLICO[p]}
                </button>
              ))}
            </div>
            <div className="mu-grade">
              <div>
                <label className="ac-campo-rotulo" htmlFor="mu-inicio">
                  Início <span className="mu-opcional">· vazio = ao publicar</span>
                </label>
                <input
                  id="mu-inicio"
                  type="datetime-local"
                  className="mu-campo"
                  value={inicio}
                  onChange={(e) => setInicio(e.target.value)}
                />
              </div>
              <div>
                <label className="ac-campo-rotulo" htmlFor="mu-fim">
                  Fim <span className="mu-opcional">· até quando vale</span>
                </label>
                <input
                  id="mu-fim"
                  type="datetime-local"
                  className="mu-campo"
                  value={fim}
                  onChange={(e) => setFim(e.target.value)}
                  aria-invalid={!periodoOk}
                />
              </div>
            </div>
            {!periodoOk && (
              <div className="rm-desfecho rm-recusada">
                <Icone nome="x" tamanho={14} traco={1.8} />
                <span>O fim precisa vir depois do início.</span>
              </div>
            )}
            <div className="mu-chaves">
              <Chave
                titulo="Fixar no topo"
                texto="Posição na lista — não muda a prioridade"
                ligada={fixado}
                aoMudar={setFixado}
              />
              <Chave
                titulo="Pedir confirmação de leitura"
                texto={'Só para avisos que realmente exigem "li e entendi"'}
                ligada={confirmacao}
                aoMudar={setConfirmacao}
              />
            </div>
            {falha && (
              <Desfecho
                escrita={falha.escrita}
                incerta={
                  falha.etapa === 'publicar'
                    ? 'O rascunho está salvo, mas não sabemos se a publicação entrou. Confira a lista antes de publicar de novo.'
                    : 'O aviso pode ter sido salvo. O texto digitado continua aqui; confira a lista antes de salvar de novo.'
                }
                aoConferir={aoConferir}
              />
            )}
            {falha?.etapa === 'publicar' && falha.escrita.tipo === 'recusada' && (
              <div className="ac-nota mu-nota-falha">
                O rascunho foi salvo; só a publicação foi recusada.
              </div>
            )}
            <div className="mu-botoes">
              <button
                type="button"
                className="ac-botao-cheio"
                disabled={!podeSalvar || !fim || falha?.escrita.tipo === 'incerta'}
                title={fim ? undefined : 'Defina até quando o aviso vale'}
                onClick={() => void enviar(true)}
              >
                {enviando === 'publicar' ? 'Publicando…' : 'Publicar aviso'}
              </button>
              <button
                type="button"
                className="ac-botao-contorno"
                disabled={!podeSalvar || falha?.escrita.tipo === 'incerta'}
                onClick={() => void enviar(false)}
              >
                {enviando === 'rascunho' ? 'Salvando…' : 'Salvar rascunho'}
              </button>
              <button
                type="button"
                className="ac-botao-contorno mu-botao-previa"
                onClick={() => setPrevia((p) => (p === 'celular' ? 'computador' : 'celular'))}
              >
                Prévia
              </button>
            </div>
            <div className="ac-pe-modal">
              {fim
                ? 'Publicado não se reescreve: para corrigir, arquive e publique outro. Tudo fica na trilha com autor e hora.'
                : 'Para publicar, defina até quando o aviso vale. Rascunho pode ficar sem data.'}
            </div>
          </div>
        </div>

        <div className="mu-previa">
          <div className="mu-previa-abas" role="tablist" aria-label="Prévia">
            <button
              type="button"
              role="tab"
              aria-selected={previa === 'celular'}
              className={previa === 'celular' ? 'mu-previa-aba-ativa' : undefined}
              onClick={() => setPrevia('celular')}
            >
              Celular
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={previa === 'computador'}
              className={previa === 'computador' ? 'mu-previa-aba-ativa' : undefined}
              onClick={() => setPrevia('computador')}
            >
              Computador
            </button>
          </div>
          {previa === 'celular' ? (
            <>
              <div className="mu-previa-rotulo">PRÉVIA · 390 × 844 · HOME DO BALCONISTA</div>
              <div className="mu-celular">
                <div className="mu-celular-topo">
                  <span className="mu-celular-marca">+</span>
                  <div style={{ flex: 1 }}>
                    <div className="mu-celular-ola">{saudacao()}</div>
                    <div className="mu-celular-dia">{hojePorExtenso()}</div>
                  </div>
                  <span className="mu-celular-sino">
                    <Icone nome="bell" tamanho={16} traco={1.5} />
                  </span>
                </div>
                <div className="mu-celular-corpo">
                  <CartaoDeAviso
                    aviso={conteudo}
                    resumido
                    confirmar={
                      confirmacao ? (
                        <button type="button" className="mu-confirmar" tabIndex={-1}>
                          Li e entendi
                        </button>
                      ) : null
                    }
                  />
                  <span className="mu-ver-todos">
                    Ver todos os avisos <Icone nome="chev-r" tamanho={13} traco={1.8} />
                  </span>
                  <div className="mu-previa-nota">
                    Três no máximo na Home, o resto na lista. Nenhum aviso impede abrir o Giro ou
                    registrar venda.
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="mu-previa-rotulo">PRÉVIA · MURAL NO COMPUTADOR</div>
              <div className="mu-computador">
                <CartaoDeAviso
                  aviso={conteudo}
                  confirmar={
                    confirmacao ? (
                      <button type="button" className="mu-confirmar" tabIndex={-1}>
                        Li e entendi
                      </button>
                    ) : null
                  }
                />
              </div>
            </>
          )}
          <div className="mu-previa-pe">
            <IconeDoTipo tipo={tipoFinal} /> {ROTULO_DO_TIPO[tipoFinal]} para{' '}
            {ROTULO_DO_PUBLICO[publico].toLowerCase()} ·{' '}
            {fim ? `vale até ${diaMesHora(deCampoDataHora(fim) ?? '')}` : 'sem fim definido'}
          </div>
        </div>
      </div>
    </div>
  );
}

function Chave({
  titulo,
  texto,
  ligada,
  aoMudar,
}: {
  titulo: string;
  texto: string;
  ligada: boolean;
  aoMudar: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={ligada}
      className="mu-chave"
      onClick={() => aoMudar(!ligada)}
    >
      <span style={{ flex: 1 }}>
        <span className="mu-chave-titulo">{titulo}</span>
        <span className="mu-chave-texto">{texto}</span>
      </span>
      <span className={`mu-interruptor${ligada ? ' mu-interruptor-ligado' : ''}`}>
        <i />
      </span>
    </button>
  );
}

function hojePorExtenso(): string {
  const d = new Date();
  const semana = d.toLocaleDateString('pt-BR', { weekday: 'long', timeZone: 'America/Sao_Paulo' });
  const resto = d.toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    timeZone: 'America/Sao_Paulo',
  });
  return `${semana.split('-')[0] ?? semana}, ${resto}`;
}
